import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  useTheme,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { getMyCourses } from "../api/courseAPI";
import { AuthContext } from "../theme/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  console.log("Auth Context:", auth.user.username);
  const navigate = useNavigate();

  const userId = auth.user ? auth.user.username : null;

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchCourses = async () => {
      try {
        const data = await getMyCourses(userId);
        console.log("Fetched Courses:", data);
        setCourses(data);
      
      } catch (error) {
        console.error("Failed to load courses", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [userId]);

  const meanProgress = useMemo(() => {
    if (!courses || courses.length === 0) return 0;

    const total = courses.reduce(
      (sum, item) => sum + (item.progress || 0),
      0
    );

    return Math.round(total / courses.length); // rounded average
  }, [courses]);


  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.default,
        flexGrow: 1,
        width: "100%",
      }}
    >
      {/* ======== SUMMARY CARDS ======== */}
      <Grid
        container
        spacing={2}
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: 3,
          width: "100%",
          borderRadius: 3,
          boxSizing: "border-box",
        }}
      >
        {/* Total Courses */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Card
            sx={{
              borderRadius: 1,
              background: "rgba(113, 152, 247, 0.10)",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "21px 27px",
                gap: 2,
              }}
            >
              <img src="./courses.png" alt="" />
              <Typography
                sx={{ fontWeight: 600, color: "#155AC1", fontSize: 24 }}
              >
                {courses.length}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>TOTAL COURSES</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Progress */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Card
            sx={{
              borderRadius: 1,
              background: "rgba(188, 149, 226, 0.15)",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "21px 27px",
                gap: 2,
              }}
            >
              <img src="./progress.png" alt="" />
              <Typography
                sx={{ fontWeight: 600, color: "#8C52FF", fontSize: 24 }}
              >
                {meanProgress}%
                
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>TOTAL PROGRESS</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Certificates */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Card
            sx={{
              borderRadius: 1,
              background: "rgba(143, 202, 139, 0.20)",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "21px 27px",
                gap: 2,
              }}
            >
              <img src="./certificates.png" />
              <Typography
                sx={{ fontWeight: 600, color: "#4CA746", fontSize: 24 }}
              >
                0
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>TOTAL CERTIFICATE</Typography>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* ========= MY COURSE SECTION ========= */}
      <Box sx={{ mt: 4, backgroundColor: theme.palette.background.paper, padding: "20px" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography sx={{ fontWeight: 400, fontSize: 16, color: theme.palette.text.primary }}>
            My course
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: theme.palette.primary.main,
              cursor: "pointer",
              fontWeight: 500,
            }}
            onClick={() => navigate("/courses")}
          >
            View All →
          </Typography>
        </Box>

        {/* Course List */}
        <Grid container spacing={3} gap={4}>
          {courses.map((course, index) => (
            <Grid size={12} key={course.courseId}
            >
              <Card
                onClick={() => navigate(`/course/${course.courseId}`, { state: course })}
                sx={{
                  p: 3.5,
                  boxShadow: "none",
                  cursor: "pointer",
                  borderBottom:
                    index === courses.length - 1
                      ? "none"
                      : `1px solid ${theme.palette.divider}`,
                  display: { xs: "block", md: "flex" },
                  gap: 2,
                  alignItems: "center",

                  //  Hover effect
                  transition: "background-color 0.25s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >

                {/* Course Image */}
                <Box
                  sx={{
                    width: 260,
                    height: 143,
                    borderRadius: 2,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={course.imageUrl?.videoUrl||""}
                    alt={course.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Details */}
                <Box sx={{ flexGrow: 1 }}>
                  <Chip
                    label={course.category}
                    size="small"
                    sx={{
                      background: theme.palette.action.selected,
                      borderRadius: 2,
                      mb: 1,
                    }}
                  />

                  <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
                    {course.title}
                  </Typography>

                  <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                    {course.organisation}
                  </Typography>

                  {/* Progress */}
                  <Typography sx={{ fontSize: 13, mt: 1 }}>
                    Complete – {course.progress}%
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={course.progress}
                    sx={{
                      height: 6,
                      borderRadius: 5,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Box>
  );
}
